import axios from "axios";
import Cookies from "js-cookie";
import { createStandaloneToast } from "@chakra-ui/react";

const { toast } = createStandaloneToast();

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api/v1",
  headers: {
    Accept: "application/json",
    // NOTE: don't set Content-Type globally; axios will set it correctly per request
  },
});

const clearWorkbookSelection = () => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem("workbook_organization_survey_id");
  sessionStorage.removeItem("organization_survey_id");
};

// Add a request interceptor to include the token in all requests
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("hkeAuthToken");
    const isSimulated = Cookies.get("simulatedMode");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isSimulated) {
      config.headers["X-HKE-Simulated"] = "true";
    }

    // ✅ If sending FormData, let the browser/axios set multipart boundaries.
    if (config.data instanceof FormData) {
      // remove any content-type that may have been set elsewhere
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
      return config;
    }

    // ✅ Default JSON behavior for normal objects
    if (config.data && typeof config.data === "object" && !(config.data instanceof Blob)) {
      config.headers["Content-Type"] = "application/json";
      // axios will JSON.stringify automatically for objects, so we don't need transformRequest
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor for tour read-only behavior
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error;
    const message = error?.response?.data?.message;
    const hasToken = !!localStorage.getItem("hkeAuthToken");
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isProtectedPath = path.startsWith("/app") || path.startsWith("/hub");
    const requestUrl = error?.config?.url || "";
    const isFeatureToggleRequest = requestUrl.includes("/feature_toggles");

    const isTourAccountsRestriction =
      typeof message === "string" && message.toLowerCase().includes("tour accounts");

    if (status === 403 && (code === "tour_read_only" || isTourAccountsRestriction)) {
      toast({
        title: "Tour Mode: Changes are disabled",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      error.__tourReadOnly = true;
    }

    if (status === 401 && hasToken && isProtectedPath && !isFeatureToggleRequest) {
      clearWorkbookSelection();
      localStorage.removeItem("hkeAuthToken");
      localStorage.removeItem("tokenCreationTime");
      delete instance.defaults.headers.common.Authorization;
      Cookies.remove("hkeAuthToken", { path: "/" });
      Cookies.remove("tokenCreationTime", { path: "/" });
      if (!path.startsWith("/tour")) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
